import { AuthStore } from "../auth";
import { LangStore } from "./LangStore";
import { PrefStore } from "./PrefStore";
import { SystemStatusStore } from "./SystemStatusStore";
import { EventSourceStore } from "./EventSourceStore";
import { registerEmailVerificationSSEHandlers } from "./emailVerificationSSEHandlers";
import { registerNotificationSSEHandlers } from "./notificationSSEHandlers";
import { MaintenanceStore } from "./MaintenanceStore";
import { setMaintenanceListener } from "../api/apiClient";
import { NotificationStore } from "./NotificationStore";
import { notificationServerSync } from "./notificationServerSync";
import {
  ReferenceCacheStore,
  CacheableEntityConfig,
} from "./ReferenceCacheStore";
import { getVatStatusValues } from "../entities/vatStatus/store";
import { getGrossReceiptsStatusValues } from "../entities/grossReceiptsStatus/store";
import { getTenantTypeValues } from "../entities/tenantType/store";
import { getTenantStatusValues } from "../entities/tenantStatus/store";
import { getCurrencyValues } from "../entities/currency/store";
import { getTenantPlanValues } from "../entities/tenantPlan/store";
import { getCountryValues } from "../entities/country/store";
import { getCreditTierValues } from "../entities/creditTier/store";
import { getReportTypeValues } from "../entities/reportType/store";
import { getCollectiveManagementSocietyValues } from "../entities/collectiveManagementSociety/store";
import { getPublisherValues } from "../entities/publisher/store";
import { getEditionTypeValues } from "../entities/editionType/store";
import { getTerritoryValues } from "../entities/territory/store";
import { getPlatformValues } from "../entities/platform/store";
import { getPlatformLinkTypeValues } from "../entities/platformLinkType/store";
import { getEditionDocumentTypeValues } from "../entities/editionDocumentType/store";
import { getMusicalGenreValues } from "../entities/musicalGenre/store";
import { getPaymentReceiptTypeValues } from "../entities/paymentReceiptType/store";
import { getLlmProviderValues } from "../entities/llmProvider/store";
import { DashboardStore } from "../dashboard/store";
import { DownloadProgressStore } from "../common/stores/DownloadProgressStore";
import { AiAssistantStore } from "../common/aiAssistant";
import { ActivityStore } from "../common/stores/ActivityStore";
import { ActivityQueueStore } from "../common/stores/ActivityQueueStore";
import { SystemMonitorStore } from "../common/stores/SystemMonitorStore";
import { FileDownloadService } from "../common/services/FileDownloadService";
import { RouterStoreAdapter } from "./RouterStoreAdapter";
import { StoreRegistry } from "./StoreRegistry";
import { BrowserSyncAdapter } from "./BrowserSyncAdapter";
import { UiStore } from "./UiStore";
import { Location } from "react-router";
import { runInAction, makeAutoObservable } from "mobx";
import { BreadcrumbCrumb } from "../router/types";
import { IPersistor } from "./persistence/IPersistor";
import { IndexedDBPersistor } from "./persistence/IndexedDBPersistor";
import { SessionStoragePersistor } from "./persistence/SessionStoragePersistor";
import {
  UserCreateStore,
  UserUpdateStore,
  UserViewStore,
  UserListStore,
} from "../entities/user/store";
import {
  RoleCreateStore,
  RoleUpdateStore,
  RoleViewStore,
  RoleListStore,
} from "../entities/role/store";
import {
  PermissionCreateStore,
  PermissionUpdateStore,
  PermissionViewStore,
  PermissionListStore,
} from "../entities/permission/store";
import {
  TenantCreateStore,
  TenantUpdateStore,
  TenantViewStore,
  TenantListStore,
} from "../entities/tenant/store";
import {
  TenantProfileCreateStore,
  TenantProfileUpdateStore,
  TenantProfileViewStore,
  TenantProfileListStore,
} from "../entities/tenantProfile/store";
import {
  VatStatusCreateStore,
  VatStatusUpdateStore,
  VatStatusViewStore,
  VatStatusListStore,
} from "../entities/vatStatus/store";
import {
  GrossReceiptsStatusCreateStore,
  GrossReceiptsStatusUpdateStore,
  GrossReceiptsStatusViewStore,
  GrossReceiptsStatusListStore,
} from "../entities/grossReceiptsStatus/store";
import {
  TenantTypeCreateStore,
  TenantTypeUpdateStore,
  TenantTypeViewStore,
  TenantTypeListStore,
} from "../entities/tenantType/store";
import {
  TenantStatusCreateStore,
  TenantStatusUpdateStore,
  TenantStatusViewStore,
  TenantStatusListStore,
} from "../entities/tenantStatus/store";
import {
  CurrencyCreateStore,
  CurrencyUpdateStore,
  CurrencyViewStore,
  CurrencyListStore,
} from "../entities/currency/store";
import {
  TenantPlanCreateStore,
  TenantPlanUpdateStore,
  TenantPlanViewStore,
  TenantPlanListStore,
} from "../entities/tenantPlan/store";
import {
  CountryCreateStore,
  CountryUpdateStore,
  CountryViewStore,
  CountryListStore,
} from "../entities/country/store";
import {
  ProvinceStateCreateStore,
  ProvinceStateUpdateStore,
  ProvinceStateViewStore,
  ProvinceStateListStore,
} from "../entities/provinceState/store";
import {
  CreditTierCreateStore,
  CreditTierUpdateStore,
  CreditTierViewStore,
  CreditTierListStore,
} from "../entities/creditTier/store";
import {
  CreditAccountUpdateStore,
  CreditAccountViewStore,
  CreditAccountListStore,
} from "../entities/creditAccount/store";
import {
  CreditPurchaseCreateStore,
  CreditPurchaseViewStore,
  CreditPurchaseListStore,
} from "../entities/creditPurchase/store";
import {
  CreditMovementViewStore,
  CreditMovementListStore,
} from "../entities/creditMovement/store";
import {
  WorkerEventViewStore,
  WorkerEventListStore,
} from "../entities/workerEvent/store";
import {
  SimulationSessionViewStore,
  SimulationSessionListStore,
} from "../entities/simulationSession/store";
import {
  SnapshotOverrideCreateStore,
  SnapshotOverrideUpdateStore,
  SnapshotOverrideViewStore,
  SnapshotOverrideListStore,
} from "../entities/snapshotOverride/store";
import { BotRunViewStore, BotRunListStore } from "../entities/botRun/store";
import {
  BotPhaseViewStore,
  BotPhaseListStore,
} from "../entities/botPhase/store";
import { BotStepViewStore, BotStepListStore } from "../entities/botStep/store";
import {
  BotErrorViewStore,
  BotErrorListStore,
} from "../entities/botError/store";
import {
  SatelliteMachineCreateStore,
  SatelliteMachineUpdateStore,
  SatelliteMachineViewStore,
  SatelliteMachineListStore,
} from "../entities/satelliteMachine/store";
import {
  SatelliteTaskViewStore,
  SatelliteTaskListStore,
} from "../entities/satelliteTask/store";
import {
  SatelliteHeartbeatViewStore,
  SatelliteHeartbeatListStore,
} from "../entities/satelliteHeartbeat/store";
import {
  DashboardWidgetViewStore,
  DashboardWidgetListStore,
} from "../entities/dashboardWidget/store";
import {
  TenantWidgetAccessCreateStore,
  TenantWidgetAccessUpdateStore,
  TenantWidgetAccessViewStore,
  TenantWidgetAccessListStore,
} from "../entities/tenantWidgetAccess/store";
import {
  UserWidgetConfigCreateStore,
  UserWidgetConfigUpdateStore,
  UserWidgetConfigViewStore,
  UserWidgetConfigListStore,
} from "../entities/userWidgetConfig/store";
import {
  DashboardLayoutTemplateCreateStore,
  DashboardLayoutTemplateUpdateStore,
  DashboardLayoutTemplateViewStore,
  DashboardLayoutTemplateListStore,
} from "../entities/dashboardLayoutTemplate/store";
import {
  ReportCreateStore,
  ReportUpdateStore,
  ReportViewStore,
  ReportListStore,
} from "../entities/report/store";
import {
  ReportTypeCreateStore,
  ReportTypeUpdateStore,
  ReportTypeViewStore,
  ReportTypeListStore,
} from "../entities/reportType/store";
import {
  UserNotificationViewStore,
  UserNotificationListStore,
} from "../entities/userNotification/store";
import {
  UserNotificationPreferenceCreateStore,
  UserNotificationPreferenceUpdateStore,
  UserNotificationPreferenceViewStore,
  UserNotificationPreferenceListStore,
} from "../entities/userNotificationPreference/store";
import {
  AlertRuleCreateStore,
  AlertRuleUpdateStore,
  AlertRuleViewStore,
  AlertRuleListStore,
} from "../entities/alertRule/store";
import {
  AlertSubscriptionCreateStore,
  AlertSubscriptionUpdateStore,
  AlertSubscriptionViewStore,
  AlertSubscriptionListStore,
} from "../entities/alertSubscription/store";
import {
  EmailVerificationCreateStore,
  EmailVerificationUpdateStore,
  EmailVerificationViewStore,
  EmailVerificationListStore,
} from "../entities/emailVerification/store";
import {
  ShowcaseWorkflowStateCreateStore,
  ShowcaseWorkflowStateUpdateStore,
  ShowcaseWorkflowStateViewStore,
  ShowcaseWorkflowStateListStore,
} from "../entities/showcaseWorkflowState/store";
import {
  ShowcaseCategoryCreateStore,
  ShowcaseCategoryUpdateStore,
  ShowcaseCategoryViewStore,
  ShowcaseCategoryListStore,
} from "../entities/showcaseCategory/store";
import {
  ShowcaseLineCreateStore,
  ShowcaseLineUpdateStore,
  ShowcaseLineViewStore,
  ShowcaseLineListStore,
} from "../entities/showcaseLine/store";
import {
  DesignShowcaseCreateStore,
  DesignShowcaseUpdateStore,
  DesignShowcaseViewStore,
  DesignShowcaseListStore,
} from "../entities/designShowcase/store";
import { ThemeViewStore, ThemeListStore } from "../entities/theme/store";
import {
  AuthorComposerCreateStore,
  AuthorComposerUpdateStore,
  AuthorComposerViewStore,
  AuthorComposerListStore,
} from "../entities/authorComposer/store";
import {
  CollectiveManagementSocietyCreateStore,
  CollectiveManagementSocietyUpdateStore,
  CollectiveManagementSocietyViewStore,
  CollectiveManagementSocietyListStore,
} from "../entities/collectiveManagementSociety/store";
import {
  AuthorCollectingSocietyCreateStore,
  AuthorCollectingSocietyUpdateStore,
  AuthorCollectingSocietyViewStore,
  AuthorCollectingSocietyListStore,
} from "../entities/authorCollectingSociety/store";
import {
  PublisherCreateStore,
  PublisherUpdateStore,
  PublisherViewStore,
  PublisherListStore,
} from "../entities/publisher/store";
import {
  AuthorPublisherCreateStore,
  AuthorPublisherUpdateStore,
  AuthorPublisherViewStore,
  AuthorPublisherListStore,
} from "../entities/authorPublisher/store";
import {
  WorkAuthorPublisherCreateStore,
  WorkAuthorPublisherUpdateStore,
  WorkAuthorPublisherViewStore,
  WorkAuthorPublisherListStore,
} from "../entities/workAuthorPublisher/store";
import {
  PublisherCollectingSocietyCreateStore,
  PublisherCollectingSocietyUpdateStore,
  PublisherCollectingSocietyViewStore,
  PublisherCollectingSocietyListStore,
} from "../entities/publisherCollectingSociety/store";
import {
  PublisherRepresentativeCreateStore,
  PublisherRepresentativeUpdateStore,
  PublisherRepresentativeViewStore,
  PublisherRepresentativeListStore,
} from "../entities/publisherRepresentative/store";
import {
  EditionTypeCreateStore,
  EditionTypeUpdateStore,
  EditionTypeViewStore,
  EditionTypeListStore,
} from "../entities/editionType/store";
import {
  MusicalWorkCreateStore,
  MusicalWorkUpdateStore,
  MusicalWorkViewStore,
  MusicalWorkListStore,
} from "../entities/musicalWork/store";
import {
  PhonogramCreateStore,
  PhonogramUpdateStore,
  PhonogramViewStore,
  PhonogramListStore,
} from "../entities/phonogram/store";
import {
  TerritoryCreateStore,
  TerritoryUpdateStore,
  TerritoryViewStore,
  TerritoryListStore,
} from "../entities/territory/store";
import {
  MusicalWorkLinkCreateStore,
  MusicalWorkLinkUpdateStore,
  MusicalWorkLinkViewStore,
  MusicalWorkLinkListStore,
} from "../entities/musicalWorkLink/store";
import {
  PlatformCreateStore,
  PlatformUpdateStore,
  PlatformViewStore,
  PlatformListStore,
} from "../entities/platform/store";
import {
  PlatformLinkTypeCreateStore,
  PlatformLinkTypeUpdateStore,
  PlatformLinkTypeViewStore,
  PlatformLinkTypeListStore,
} from "../entities/platformLinkType/store";
import {
  WorkAuthorCreateStore,
  WorkAuthorUpdateStore,
  WorkAuthorViewStore,
  WorkAuthorListStore,
} from "../entities/workAuthor/store";
import {
  EditionCreateStore,
  EditionUpdateStore,
  EditionViewStore,
  EditionListStore,
} from "../entities/edition/store";
import {
  SignedEditionViewStore,
  SignedEditionListStore,
} from "../entities/signedEdition/store";
import {
  EditionContractPublisherViewStore,
  EditionContractPublisherListStore,
} from "../entities/editionContractPublisher/store";
import {
  EditionDocumentationViewStore,
  EditionDocumentationListStore,
} from "../entities/editionDocumentation/store";
import {
  SignedContractViewStore,
  SignedContractListStore,
} from "../entities/signedContract/store";
import {
  EditionDocumentTypeCreateStore,
  EditionDocumentTypeUpdateStore,
  EditionDocumentTypeViewStore,
  EditionDocumentTypeListStore,
} from "../entities/editionDocumentType/store";
import {
  AssociatedDocumentEditionViewStore,
  AssociatedDocumentEditionListStore,
} from "../entities/associatedDocumentEdition/store";
import {
  AssociatedDocumentSignedEditionViewStore,
  AssociatedDocumentSignedEditionListStore,
} from "../entities/associatedDocumentSignedEdition/store";
import {
  SignedEditionBlockchainCertViewStore,
  SignedEditionBlockchainCertListStore,
} from "../entities/signedEditionBlockchainCert/store";
import {
  UserAttachmentEditionCreateStore,
  UserAttachmentEditionUpdateStore,
  UserAttachmentEditionViewStore,
  UserAttachmentEditionListStore,
} from "../entities/userAttachmentEdition/store";
import {
  InterpreterCreateStore,
  InterpreterUpdateStore,
  InterpreterViewStore,
  InterpreterListStore,
} from "../entities/interpreter/store";
import {
  MusicalGenreCreateStore,
  MusicalGenreUpdateStore,
  MusicalGenreViewStore,
  MusicalGenreListStore,
} from "../entities/musicalGenre/store";
import {
  ContractObservationCreateStore,
  ContractObservationUpdateStore,
  ContractObservationViewStore,
  ContractObservationListStore,
} from "../entities/contractObservation/store";
import {
  PaymentReceiptTypeCreateStore,
  PaymentReceiptTypeUpdateStore,
  PaymentReceiptTypeViewStore,
  PaymentReceiptTypeListStore,
} from "../entities/paymentReceiptType/store";
import {
  PaymentReceiptUseViewStore,
  PaymentReceiptUseListStore,
} from "../entities/paymentReceiptUse/store";
import {
  PaymentReceiptCreateStore,
  PaymentReceiptUpdateStore,
  PaymentReceiptViewStore,
  PaymentReceiptListStore,
} from "../entities/paymentReceipt/store";
import {
  TadManagerConfigUpdateStore,
  TadManagerConfigViewStore,
  TadManagerConfigListStore,
} from "../entities/tadManagerConfig/store";
import {
  DocumentGenerationParamsCreateStore,
  DocumentGenerationParamsUpdateStore,
  DocumentGenerationParamsViewStore,
  DocumentGenerationParamsListStore,
} from "../entities/documentGenerationParams/store";
import {
  EditionDocumentationWorkflowStateCreateStore,
  EditionDocumentationWorkflowStateUpdateStore,
  EditionDocumentationWorkflowStateViewStore,
  EditionDocumentationWorkflowStateListStore,
} from "../entities/editionDocumentationWorkflowState/store";
import {
  SignedEditionDndaWorkflowStateCreateStore,
  SignedEditionDndaWorkflowStateUpdateStore,
  SignedEditionDndaWorkflowStateViewStore,
  SignedEditionDndaWorkflowStateListStore,
} from "../entities/signedEditionDndaWorkflowState/store";
import {
  SignedEditionSadaicWorkflowStateCreateStore,
  SignedEditionSadaicWorkflowStateUpdateStore,
  SignedEditionSadaicWorkflowStateViewStore,
  SignedEditionSadaicWorkflowStateListStore,
} from "../entities/signedEditionSadaicWorkflowState/store";
import {
  LlmProviderCreateStore,
  LlmProviderUpdateStore,
  LlmProviderViewStore,
  LlmProviderListStore,
} from "../entities/llmProvider/store";
import {
  TenantLlmConfigCreateStore,
  TenantLlmConfigUpdateStore,
  TenantLlmConfigViewStore,
  TenantLlmConfigListStore,
} from "../entities/tenantLlmConfig/store";
import {
  TenantDocumentCreateStore,
  TenantDocumentUpdateStore,
  TenantDocumentViewStore,
  TenantDocumentListStore,
} from "../entities/tenantDocument/store";
import {
  AiSearchConversationViewStore,
  AiSearchConversationListStore,
} from "../entities/aiSearchConversation/store";
import {
  AiSearchResponseCreateStore,
  AiSearchResponseViewStore,
  AiSearchResponseListStore,
} from "../entities/aiSearchResponse/store";
import {
  AiSearchToolCallCreateStore,
  AiSearchToolCallUpdateStore,
  AiSearchToolCallViewStore,
  AiSearchToolCallListStore,
} from "../entities/aiSearchToolCall/store";
import {
  TenantTokenUsageViewStore,
  TenantTokenUsageListStore,
} from "../entities/tenantTokenUsage/store";
import {
  TenantStorageUsageViewStore,
  TenantStorageUsageListStore,
} from "../entities/tenantStorageUsage/store";
import {
  AiSearchAutoEvalViewStore,
  AiSearchAutoEvalListStore,
} from "../entities/aiSearchAutoEval/store";
import {
  AiSearchPatternCreateStore,
  AiSearchPatternUpdateStore,
  AiSearchPatternViewStore,
  AiSearchPatternListStore,
} from "../entities/aiSearchPattern/store";
import {
  EvaluationQueryCreateStore,
  EvaluationQueryUpdateStore,
  EvaluationQueryViewStore,
  EvaluationQueryListStore,
} from "../entities/evaluationQuery/store";
import {
  QueryPatternCreateStore,
  QueryPatternUpdateStore,
  QueryPatternViewStore,
  QueryPatternListStore,
} from "../entities/queryPattern/store";
import {
  EsignatureConfigCreateStore,
  EsignatureConfigUpdateStore,
  EsignatureConfigViewStore,
  EsignatureConfigListStore,
} from "../entities/esignatureConfig/store";
import {
  EsignatureDocumentViewStore,
  EsignatureDocumentListStore,
} from "../entities/esignatureDocument/store";
import {
  EsignatureRecipientViewStore,
  EsignatureRecipientListStore,
} from "../entities/esignatureRecipient/store";
import {
  EsignatureWebhookEventViewStore,
  EsignatureWebhookEventListStore,
} from "../entities/esignatureWebhookEvent/store";

export interface PersistenceEvent {
  timestamp: number;
  action: "SAVE" | "LOAD" | "CLEAR" | "PUSH" | "POP" | "SYNC" | "VALIDATE";
  data: any;
  stackTrace?: string;
}

export class RootStore {
  storeRegistry: StoreRegistry;

  langStore: LangStore;
  authStore: AuthStore;
  prefStore: PrefStore;
  uiStore: UiStore;
  systemStatusStore: SystemStatusStore;
  eventSourceStore: EventSourceStore;
  maintenanceStore: MaintenanceStore;
  notificationStore: NotificationStore;
  downloadProgressStore: DownloadProgressStore;
  activityStore: ActivityStore;
  activityQueueStore: ActivityQueueStore;
  systemMonitorStore: SystemMonitorStore;
  fileDownloadService: FileDownloadService;
  dashboardStore: DashboardStore;
  referenceCacheStore: ReferenceCacheStore;
  aiAssistantStore: AiAssistantStore;

  location: Location | null = null;
  router: RouterStoreAdapter;
  browserSyncAdapter: BrowserSyncAdapter;

  persistor: IPersistor;
  useIndexedDB: boolean = false;
  private isLoadingBreadcrumbs: boolean = true;

  breadcrumbStack: BreadcrumbCrumb[] = [];

  persistenceDebug = {
    lastSaveTimestamp: null as number | null,
    lastLoadTimestamp: null as number | null,
    lastSaveData: null as BreadcrumbCrumb[] | null,
    lastLoadData: null as BreadcrumbCrumb[] | null,
    events: [] as PersistenceEvent[],
  };

  generalErrorOpen: boolean = false;
  generalErrorTitle: string = "";
  generalErrorDescription: string = "";

  snackbarOpen: boolean = false;
  snackbarMessage: string = "";
  snackbarSeverity: "success" | "info" | "warning" | "error" = "info";

  // Drawer View state
  drawerViewOpen: boolean = false;
  drawerViewStack: {
    entityType: string;
    entityId: string;
    mode: "view" | "update";
  }[] = [];

  // Modal View state
  modalViewOpen: boolean = false;
  modalViewStack: {
    entityType: string;
    entityId: string;
    mode: "view" | "update";
  }[] = [];

  private storeConstructors: Map<string, any> = new Map();

  constructor() {
    // Initialize persistor FIRST (before any store that might use it)
    if (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("feature.disable-indexeddb") === "true"
    ) {
      this.useIndexedDB = false;
      this.persistor = new SessionStoragePersistor();
    } else {
      this.useIndexedDB = true;
      this.persistor = new IndexedDBPersistor("app-db");
    }

    this.langStore = new LangStore();
    this.storeRegistry = new StoreRegistry();
    this.activityStore = new ActivityStore();
    this.activityQueueStore = new ActivityQueueStore();
    this.authStore = new AuthStore(this);
    this.prefStore = new PrefStore(this);
    this.uiStore = new UiStore(this);
    this.systemStatusStore = new SystemStatusStore(this);
    this.eventSourceStore = new EventSourceStore(this);
    this.maintenanceStore = new MaintenanceStore(this);
    // Leer y quitar un aviso del server llegan al server: si no, sigue sin leer
    // y el server lo reenvía en cada conexión SSE.
    this.notificationStore = new NotificationStore(
      this,
      notificationServerSync(this)
    );
    this.downloadProgressStore = new DownloadProgressStore();
    this.systemMonitorStore = new SystemMonitorStore(this);
    this.fileDownloadService = new FileDownloadService(this);
    this.dashboardStore = new DashboardStore(this);
    this.aiAssistantStore = new AiAssistantStore(this);

    // Initialize ReferenceCacheStore with cacheable entity configs
    const cacheableConfigs: CacheableEntityConfig[] = [
      {
        entityName: "VatStatus",
        apiListMethod: "vatStatusListWithOffset",
        getValuesFunc: getVatStatusValues,
        orderBy: undefined,
      },
      {
        entityName: "GrossReceiptsStatus",
        apiListMethod: "grossReceiptsStatusListWithOffset",
        getValuesFunc: getGrossReceiptsStatusValues,
        orderBy: undefined,
      },
      {
        entityName: "TenantType",
        apiListMethod: "tenantTypeListWithOffset",
        getValuesFunc: getTenantTypeValues,
        orderBy: undefined,
      },
      {
        entityName: "TenantStatus",
        apiListMethod: "tenantStatusListWithOffset",
        getValuesFunc: getTenantStatusValues,
        orderBy: undefined,
      },
      {
        entityName: "Currency",
        apiListMethod: "currencyListWithOffset",
        getValuesFunc: getCurrencyValues,
        orderBy: undefined,
      },
      {
        entityName: "TenantPlan",
        apiListMethod: "tenantPlanListWithOffset",
        getValuesFunc: getTenantPlanValues,
        orderBy: undefined,
      },
      {
        entityName: "Country",
        apiListMethod: "countryListWithOffset",
        getValuesFunc: getCountryValues,
        orderBy: [{ [this.langStore.fieldByLang("name")]: "asc" }],
      },
      {
        entityName: "CreditTier",
        apiListMethod: "creditTierListWithOffset",
        getValuesFunc: getCreditTierValues,
        orderBy: [{ monthlyCredits: "asc" }],
      },
      {
        entityName: "ReportType",
        apiListMethod: "reportTypeListWithOffset",
        getValuesFunc: getReportTypeValues,
        orderBy: undefined,
      },
      {
        entityName: "CollectiveManagementSociety",
        apiListMethod: "collectiveManagementSocietyListWithOffset",
        getValuesFunc: getCollectiveManagementSocietyValues,
        orderBy: undefined,
      },
      {
        entityName: "Publisher",
        apiListMethod: "publisherListWithOffset",
        getValuesFunc: getPublisherValues,
        orderBy: [{ lastUpdatedAt_: "desc" }],
      },
      {
        entityName: "EditionType",
        apiListMethod: "editionTypeListWithOffset",
        getValuesFunc: getEditionTypeValues,
        orderBy: undefined,
      },
      {
        entityName: "Territory",
        apiListMethod: "territoryListWithOffset",
        getValuesFunc: getTerritoryValues,
        orderBy: [{ [this.langStore.fieldByLang("name")]: "asc" }],
      },
      {
        entityName: "Platform",
        apiListMethod: "platformListWithOffset",
        getValuesFunc: getPlatformValues,
        orderBy: undefined,
      },
      {
        entityName: "PlatformLinkType",
        apiListMethod: "platformLinkTypeListWithOffset",
        getValuesFunc: getPlatformLinkTypeValues,
        orderBy: undefined,
      },
      {
        entityName: "EditionDocumentType",
        apiListMethod: "editionDocumentTypeListWithOffset",
        getValuesFunc: getEditionDocumentTypeValues,
        orderBy: undefined,
      },
      {
        entityName: "MusicalGenre",
        apiListMethod: "musicalGenreListWithOffset",
        getValuesFunc: getMusicalGenreValues,
        orderBy: undefined,
      },
      {
        entityName: "PaymentReceiptType",
        apiListMethod: "paymentReceiptTypeListWithOffset",
        getValuesFunc: getPaymentReceiptTypeValues,
        orderBy: undefined,
      },
      {
        entityName: "LlmProvider",
        apiListMethod: "llmProviderListWithOffset",
        getValuesFunc: getLlmProviderValues,
        orderBy: [{ name: "asc" }],
      },
    ];
    this.referenceCacheStore = new ReferenceCacheStore(this, cacheableConfigs);

    // Modo mantenimiento: broadcast SSE (aviso previo / activacion / fin) +
    // deteccion de 503 {maintenance: true} en cualquier request del apiClient.
    this.eventSourceStore.registerHandler("maintenance", (payload: any) => {
      if (payload?.active === false) {
        this.maintenanceStore.clear();
      } else if (
        payload?.until &&
        new Date(payload.until).getTime() > Date.now()
      ) {
        this.maintenanceStore.warn(payload);
      } else {
        this.maintenanceStore.activate(payload ?? {});
      }
    });
    setMaintenanceListener((info) => this.maintenanceStore.activate(info));

    // Register SSE handler for cache invalidation
    this.eventSourceStore.registerHandler(
      "reference.cache.invalidate",
      (payload: { entityName: string }) => {
        this.referenceCacheStore.invalidate(payload.entityName);
      }
    );

    // Register SSE handlers for persistent notifications
    registerNotificationSSEHandlers(this);

    // Register email verification SSE handlers
    registerEmailVerificationSSEHandlers(this);

    this.router = new RouterStoreAdapter();
    this.router.setRootStore(this);

    this.browserSyncAdapter = new BrowserSyncAdapter(this);
    this.browserSyncAdapter.setupBrowserSync();

    this.registerStoreConstructors();

    this.storeRegistry.onStoreReady((store) => {
      const breadcrumbLabel = (store as any).breadcrumbLabel;
      const currentPathname = this.router.pathname;
      const callbackReturnPath = (store as any).callbackReturnPath;

      // Don't push breadcrumb if store is in "Add new" flow (will close and return)
      if (callbackReturnPath) {
        return;
      }

      if (breadcrumbLabel && currentPathname && currentPathname !== "/") {
        const isInStack = this.breadcrumbStack.some(
          (b) => b.pathname === currentPathname
        );
        if (!isInStack) {
          this.pushBreadcrumb(breadcrumbLabel, currentPathname);
        }
      }
    });

    this.loadBreadcrumbs();

    makeAutoObservable(this);
  }

  getCurrentPathname() {
    return this.location?.pathname || "/";
  }

  getWebPageId() {
    return this.location?.pathname || "";
  }

  updateLocation(location: Location) {
    runInAction(() => {
      this.location = location;
    });
  }

  showSnackbar(
    message: string,
    severity: "success" | "info" | "warning" | "error" = "info"
  ) {
    runInAction(() => {
      this.snackbarMessage = message;
      this.snackbarSeverity = severity;
      this.snackbarOpen = true;
    });
  }

  closeSnackbar() {
    runInAction(() => {
      this.snackbarOpen = false;
    });
  }

  // Drawer View methods
  openDrawerView(
    entityType: string,
    entityId: string,
    mode: "view" | "update" = "view"
  ) {
    runInAction(() => {
      this.drawerViewStack = [{ entityType, entityId, mode }];
      this.drawerViewOpen = true;
    });
  }

  pushDrawerView(
    entityType: string,
    entityId: string,
    mode: "view" | "update" = "view"
  ) {
    runInAction(() => {
      this.drawerViewStack.push({ entityType, entityId, mode });
    });
  }

  popDrawerView() {
    runInAction(() => {
      if (this.drawerViewStack.length > 1) {
        this.drawerViewStack.pop();
      }
    });
  }

  closeDrawerView() {
    runInAction(() => {
      this.drawerViewOpen = false;
      this.drawerViewStack = [];
    });
  }

  // Modal View methods
  openModalView(
    entityType: string,
    entityId: string,
    mode: "view" | "update" = "view"
  ) {
    runInAction(() => {
      this.modalViewStack = [{ entityType, entityId, mode }];
      this.modalViewOpen = true;
    });
  }

  // Subentity row click: open the modal in 'update' mode if the user has
  // permission to update that entity type, otherwise fall back to 'view'.
  // Stacks on top of any open overlay so the back arrow can return.
  openSubentityModal(entityType: string, entityId: string) {
    // Si el overlay padre está en 'view', la subentidad hereda 'view' (el modo
    // read-only cascada por toda la cadena, sin importar permisos). Si el padre
    // está en 'update', se decide por permiso como antes.
    const parent = this.drawerViewOpen
      ? this.drawerViewStack[this.drawerViewStack.length - 1]
      : this.modalViewOpen
      ? this.modalViewStack[this.modalViewStack.length - 1]
      : undefined;
    const parentIsView = parent?.mode === "view";
    const canUpdate =
      !parentIsView && this.authStore.hasPermissions(`${entityType}_update`);
    this.openOrPushOverlay(entityType, entityId, canUpdate ? "update" : "view");
  }

  // Open a new overlay, or stack on top of the current one. Used by chip
  // clicks (viewForm{X}) and subentity row clicks so navigation between
  // referenced entities builds a navigable stack instead of replacing the
  // current modal — the ModalView's back arrow then returns to the parent.
  openOrPushOverlay(
    entityType: string,
    entityId: string,
    mode: "view" | "update" = "view"
  ) {
    if (this.drawerViewOpen) {
      this.pushDrawerView(entityType, entityId, mode);
    } else if (this.modalViewOpen) {
      this.pushModalView(entityType, entityId, mode);
    } else {
      this.openModalView(entityType, entityId, mode);
    }
  }

  pushModalView(
    entityType: string,
    entityId: string,
    mode: "view" | "update" = "view"
  ) {
    runInAction(() => {
      this.modalViewStack.push({ entityType, entityId, mode });
    });
  }

  popModalView() {
    runInAction(() => {
      if (this.modalViewStack.length > 1) {
        this.modalViewStack.pop();
      }
    });
  }

  closeModalView() {
    runInAction(() => {
      this.modalViewOpen = false;
      this.modalViewStack = [];
    });
  }

  private logPersistenceEvent(
    action: PersistenceEvent["action"],
    data: any,
    includeStackTrace = false
  ) {
    const event: PersistenceEvent = {
      timestamp: Date.now(),
      action,
      data: JSON.parse(JSON.stringify(data)),
    };

    if (includeStackTrace) {
      try {
        throw new Error();
      } catch (e: any) {
        event.stackTrace = e.stack;
      }
    }

    runInAction(() => {
      this.persistenceDebug.events.push(event);
      if (this.persistenceDebug.events.length > 20) {
        this.persistenceDebug.events.shift();
      }
    });
  }

  private saveBreadcrumbs() {
    if (this.isLoadingBreadcrumbs) {
      this.logPersistenceEvent("SAVE", {
        skipped: true,
        reason: "isLoadingBreadcrumbs",
      });
      return;
    }

    const dataToSave = this.breadcrumbStack.map((crumb) => ({
      label: crumb.label,
      pathname: crumb.pathname,
      clickable: crumb.clickable,
    }));
    this.logPersistenceEvent("SAVE", { stack: dataToSave });

    runInAction(() => {
      this.persistenceDebug.lastSaveTimestamp = Date.now();
      this.persistenceDebug.lastSaveData = dataToSave;
    });

    this.persistor.set("breadcrumbs_history", dataToSave).catch((error) => {
      console.error("[DEBUG-RootStore] Failed to save breadcrumbs", error);
      this.logPersistenceEvent("SAVE", { error: error.message });
    });
  }

  async loadBreadcrumbs() {
    this.logPersistenceEvent("LOAD", { started: true });

    try {
      const stored = await this.persistor.get<BreadcrumbCrumb[]>(
        "breadcrumbs_history"
      );

      this.logPersistenceEvent("LOAD", { success: true, stack: stored });

      if (stored) {
        runInAction(() => {
          this.breadcrumbStack = stored.map((crumb, index) => ({
            label: crumb.label,
            pathname: crumb.pathname,
            clickable:
              crumb.clickable !== undefined
                ? crumb.clickable
                : index < stored.length - 1,
          }));
          this.isLoadingBreadcrumbs = false;
          this.persistenceDebug.lastLoadTimestamp = Date.now();
          this.persistenceDebug.lastLoadData = stored;
        });
      } else {
        runInAction(() => {
          this.isLoadingBreadcrumbs = false;
          this.persistenceDebug.lastLoadTimestamp = Date.now();
          this.persistenceDebug.lastLoadData = null;
        });
      }
    } catch (error: any) {
      console.error("[DEBUG-RootStore] Failed to load breadcrumbs", error);
      this.logPersistenceEvent("LOAD", { error: error.message });

      runInAction(() => {
        this.breadcrumbStack = [];
        this.isLoadingBreadcrumbs = false;
      });
    }
  }

  validateAndSyncBreadcrumbs(currentPathname: string) {
    if (this.breadcrumbStack.length === 0) {
      this.logPersistenceEvent("VALIDATE", {
        result: "empty_stack",
        pathname: currentPathname,
      });
      return;
    }

    const last = this.breadcrumbStack[this.breadcrumbStack.length - 1];

    if (last.pathname === currentPathname) {
      this.logPersistenceEvent("VALIDATE", {
        result: "consistent",
        pathname: currentPathname,
      });
      return;
    }

    const index = this.breadcrumbStack.findIndex(
      (b) => b.pathname === currentPathname
    );

    if (index >= 0) {
      this.logPersistenceEvent("VALIDATE", {
        result: "truncate",
        from: last.pathname,
        to: currentPathname,
        index,
      });
      this.popToBreadcrumb(index);
    } else if (currentPathname === "/") {
      this.logPersistenceEvent("VALIDATE", {
        result: "clear_home",
        previousPathname: last.pathname,
      });
      this.clearBreadcrumbs();
    } else {
      this.logPersistenceEvent("VALIDATE", {
        result: "clear_new_navigation",
        previousPathname: last.pathname,
        currentPathname,
      });
      this.clearBreadcrumbs();
    }
  }

  syncBreadcrumbsWithBrowser(pathname: string) {
    const index = this.breadcrumbStack.findIndex(
      (crumb) => crumb.pathname === pathname
    );

    if (index >= 0) {
      this.logPersistenceEvent("SYNC", { pathname, index, found: true });
      this.popToBreadcrumb(index);
    } else {
      this.logPersistenceEvent("SYNC", { pathname, found: false });
    }
  }

  pushBreadcrumb(label: string, pathname: string) {
    runInAction(() => {
      const lastCrumb = this.breadcrumbStack[this.breadcrumbStack.length - 1];

      if (lastCrumb?.pathname === pathname) {
        this.logPersistenceEvent("PUSH", {
          label,
          pathname,
          skipped: true,
          reason: "duplicate",
        });
        return;
      }

      if (lastCrumb) {
        lastCrumb.clickable = true;
      }

      this.logPersistenceEvent("PUSH", { label, pathname });
      this.breadcrumbStack.push({ label, pathname, clickable: false });
      this.saveBreadcrumbs();
    });
  }

  clearBreadcrumbs() {
    this.logPersistenceEvent(
      "CLEAR",
      { previousLength: this.breadcrumbStack.length },
      true
    );
    runInAction(() => {
      this.breadcrumbStack = [];
      this.saveBreadcrumbs();
    });
  }

  popToBreadcrumb(index: number) {
    runInAction(() => {
      this.breadcrumbStack = this.breadcrumbStack.slice(0, index + 1);

      const last = this.breadcrumbStack[this.breadcrumbStack.length - 1];
      if (last) {
        last.clickable = false;
      }

      for (let i = 0; i < this.breadcrumbStack.length - 1; i++) {
        this.breadcrumbStack[i].clickable = true;
      }

      this.logPersistenceEvent("POP", {
        index,
        newLength: this.breadcrumbStack.length,
      });
      this.saveBreadcrumbs();
    });
  }

  private registerStoreConstructors() {
    this.storeConstructors.set("UserCreateStore", UserCreateStore);
    this.storeConstructors.set("UserUpdateStore", UserUpdateStore);
    this.storeConstructors.set("UserViewStore", UserViewStore);
    this.storeConstructors.set("UserListStore", UserListStore);
    this.storeConstructors.set("RoleCreateStore", RoleCreateStore);
    this.storeConstructors.set("RoleUpdateStore", RoleUpdateStore);
    this.storeConstructors.set("RoleViewStore", RoleViewStore);
    this.storeConstructors.set("RoleListStore", RoleListStore);
    this.storeConstructors.set("PermissionCreateStore", PermissionCreateStore);
    this.storeConstructors.set("PermissionUpdateStore", PermissionUpdateStore);
    this.storeConstructors.set("PermissionViewStore", PermissionViewStore);
    this.storeConstructors.set("PermissionListStore", PermissionListStore);
    this.storeConstructors.set("TenantCreateStore", TenantCreateStore);
    this.storeConstructors.set("TenantUpdateStore", TenantUpdateStore);
    this.storeConstructors.set("TenantViewStore", TenantViewStore);
    this.storeConstructors.set("TenantListStore", TenantListStore);
    this.storeConstructors.set(
      "TenantProfileCreateStore",
      TenantProfileCreateStore
    );
    this.storeConstructors.set(
      "TenantProfileUpdateStore",
      TenantProfileUpdateStore
    );
    this.storeConstructors.set(
      "TenantProfileViewStore",
      TenantProfileViewStore
    );
    this.storeConstructors.set(
      "TenantProfileListStore",
      TenantProfileListStore
    );
    this.storeConstructors.set("VatStatusCreateStore", VatStatusCreateStore);
    this.storeConstructors.set("VatStatusUpdateStore", VatStatusUpdateStore);
    this.storeConstructors.set("VatStatusViewStore", VatStatusViewStore);
    this.storeConstructors.set("VatStatusListStore", VatStatusListStore);
    this.storeConstructors.set(
      "GrossReceiptsStatusCreateStore",
      GrossReceiptsStatusCreateStore
    );
    this.storeConstructors.set(
      "GrossReceiptsStatusUpdateStore",
      GrossReceiptsStatusUpdateStore
    );
    this.storeConstructors.set(
      "GrossReceiptsStatusViewStore",
      GrossReceiptsStatusViewStore
    );
    this.storeConstructors.set(
      "GrossReceiptsStatusListStore",
      GrossReceiptsStatusListStore
    );
    this.storeConstructors.set("TenantTypeCreateStore", TenantTypeCreateStore);
    this.storeConstructors.set("TenantTypeUpdateStore", TenantTypeUpdateStore);
    this.storeConstructors.set("TenantTypeViewStore", TenantTypeViewStore);
    this.storeConstructors.set("TenantTypeListStore", TenantTypeListStore);
    this.storeConstructors.set(
      "TenantStatusCreateStore",
      TenantStatusCreateStore
    );
    this.storeConstructors.set(
      "TenantStatusUpdateStore",
      TenantStatusUpdateStore
    );
    this.storeConstructors.set("TenantStatusViewStore", TenantStatusViewStore);
    this.storeConstructors.set("TenantStatusListStore", TenantStatusListStore);
    this.storeConstructors.set("CurrencyCreateStore", CurrencyCreateStore);
    this.storeConstructors.set("CurrencyUpdateStore", CurrencyUpdateStore);
    this.storeConstructors.set("CurrencyViewStore", CurrencyViewStore);
    this.storeConstructors.set("CurrencyListStore", CurrencyListStore);
    this.storeConstructors.set("TenantPlanCreateStore", TenantPlanCreateStore);
    this.storeConstructors.set("TenantPlanUpdateStore", TenantPlanUpdateStore);
    this.storeConstructors.set("TenantPlanViewStore", TenantPlanViewStore);
    this.storeConstructors.set("TenantPlanListStore", TenantPlanListStore);
    this.storeConstructors.set("CountryCreateStore", CountryCreateStore);
    this.storeConstructors.set("CountryUpdateStore", CountryUpdateStore);
    this.storeConstructors.set("CountryViewStore", CountryViewStore);
    this.storeConstructors.set("CountryListStore", CountryListStore);
    this.storeConstructors.set(
      "ProvinceStateCreateStore",
      ProvinceStateCreateStore
    );
    this.storeConstructors.set(
      "ProvinceStateUpdateStore",
      ProvinceStateUpdateStore
    );
    this.storeConstructors.set(
      "ProvinceStateViewStore",
      ProvinceStateViewStore
    );
    this.storeConstructors.set(
      "ProvinceStateListStore",
      ProvinceStateListStore
    );
    this.storeConstructors.set("CreditTierCreateStore", CreditTierCreateStore);
    this.storeConstructors.set("CreditTierUpdateStore", CreditTierUpdateStore);
    this.storeConstructors.set("CreditTierViewStore", CreditTierViewStore);
    this.storeConstructors.set("CreditTierListStore", CreditTierListStore);
    this.storeConstructors.set(
      "CreditAccountUpdateStore",
      CreditAccountUpdateStore
    );
    this.storeConstructors.set(
      "CreditAccountViewStore",
      CreditAccountViewStore
    );
    this.storeConstructors.set(
      "CreditAccountListStore",
      CreditAccountListStore
    );
    this.storeConstructors.set(
      "CreditPurchaseCreateStore",
      CreditPurchaseCreateStore
    );
    this.storeConstructors.set(
      "CreditPurchaseViewStore",
      CreditPurchaseViewStore
    );
    this.storeConstructors.set(
      "CreditPurchaseListStore",
      CreditPurchaseListStore
    );
    this.storeConstructors.set(
      "CreditMovementViewStore",
      CreditMovementViewStore
    );
    this.storeConstructors.set(
      "CreditMovementListStore",
      CreditMovementListStore
    );
    this.storeConstructors.set("WorkerEventViewStore", WorkerEventViewStore);
    this.storeConstructors.set("WorkerEventListStore", WorkerEventListStore);
    this.storeConstructors.set(
      "SimulationSessionViewStore",
      SimulationSessionViewStore
    );
    this.storeConstructors.set(
      "SimulationSessionListStore",
      SimulationSessionListStore
    );
    this.storeConstructors.set(
      "SnapshotOverrideCreateStore",
      SnapshotOverrideCreateStore
    );
    this.storeConstructors.set(
      "SnapshotOverrideUpdateStore",
      SnapshotOverrideUpdateStore
    );
    this.storeConstructors.set(
      "SnapshotOverrideViewStore",
      SnapshotOverrideViewStore
    );
    this.storeConstructors.set(
      "SnapshotOverrideListStore",
      SnapshotOverrideListStore
    );
    this.storeConstructors.set("BotRunViewStore", BotRunViewStore);
    this.storeConstructors.set("BotRunListStore", BotRunListStore);
    this.storeConstructors.set("BotPhaseViewStore", BotPhaseViewStore);
    this.storeConstructors.set("BotPhaseListStore", BotPhaseListStore);
    this.storeConstructors.set("BotStepViewStore", BotStepViewStore);
    this.storeConstructors.set("BotStepListStore", BotStepListStore);
    this.storeConstructors.set("BotErrorViewStore", BotErrorViewStore);
    this.storeConstructors.set("BotErrorListStore", BotErrorListStore);
    this.storeConstructors.set(
      "SatelliteMachineCreateStore",
      SatelliteMachineCreateStore
    );
    this.storeConstructors.set(
      "SatelliteMachineUpdateStore",
      SatelliteMachineUpdateStore
    );
    this.storeConstructors.set(
      "SatelliteMachineViewStore",
      SatelliteMachineViewStore
    );
    this.storeConstructors.set(
      "SatelliteMachineListStore",
      SatelliteMachineListStore
    );
    this.storeConstructors.set(
      "SatelliteTaskViewStore",
      SatelliteTaskViewStore
    );
    this.storeConstructors.set(
      "SatelliteTaskListStore",
      SatelliteTaskListStore
    );
    this.storeConstructors.set(
      "SatelliteHeartbeatViewStore",
      SatelliteHeartbeatViewStore
    );
    this.storeConstructors.set(
      "SatelliteHeartbeatListStore",
      SatelliteHeartbeatListStore
    );
    this.storeConstructors.set(
      "DashboardWidgetViewStore",
      DashboardWidgetViewStore
    );
    this.storeConstructors.set(
      "DashboardWidgetListStore",
      DashboardWidgetListStore
    );
    this.storeConstructors.set(
      "TenantWidgetAccessCreateStore",
      TenantWidgetAccessCreateStore
    );
    this.storeConstructors.set(
      "TenantWidgetAccessUpdateStore",
      TenantWidgetAccessUpdateStore
    );
    this.storeConstructors.set(
      "TenantWidgetAccessViewStore",
      TenantWidgetAccessViewStore
    );
    this.storeConstructors.set(
      "TenantWidgetAccessListStore",
      TenantWidgetAccessListStore
    );
    this.storeConstructors.set(
      "UserWidgetConfigCreateStore",
      UserWidgetConfigCreateStore
    );
    this.storeConstructors.set(
      "UserWidgetConfigUpdateStore",
      UserWidgetConfigUpdateStore
    );
    this.storeConstructors.set(
      "UserWidgetConfigViewStore",
      UserWidgetConfigViewStore
    );
    this.storeConstructors.set(
      "UserWidgetConfigListStore",
      UserWidgetConfigListStore
    );
    this.storeConstructors.set(
      "DashboardLayoutTemplateCreateStore",
      DashboardLayoutTemplateCreateStore
    );
    this.storeConstructors.set(
      "DashboardLayoutTemplateUpdateStore",
      DashboardLayoutTemplateUpdateStore
    );
    this.storeConstructors.set(
      "DashboardLayoutTemplateViewStore",
      DashboardLayoutTemplateViewStore
    );
    this.storeConstructors.set(
      "DashboardLayoutTemplateListStore",
      DashboardLayoutTemplateListStore
    );
    this.storeConstructors.set("ReportCreateStore", ReportCreateStore);
    this.storeConstructors.set("ReportUpdateStore", ReportUpdateStore);
    this.storeConstructors.set("ReportViewStore", ReportViewStore);
    this.storeConstructors.set("ReportListStore", ReportListStore);
    this.storeConstructors.set("ReportTypeCreateStore", ReportTypeCreateStore);
    this.storeConstructors.set("ReportTypeUpdateStore", ReportTypeUpdateStore);
    this.storeConstructors.set("ReportTypeViewStore", ReportTypeViewStore);
    this.storeConstructors.set("ReportTypeListStore", ReportTypeListStore);
    this.storeConstructors.set(
      "UserNotificationViewStore",
      UserNotificationViewStore
    );
    this.storeConstructors.set(
      "UserNotificationListStore",
      UserNotificationListStore
    );
    this.storeConstructors.set(
      "UserNotificationPreferenceCreateStore",
      UserNotificationPreferenceCreateStore
    );
    this.storeConstructors.set(
      "UserNotificationPreferenceUpdateStore",
      UserNotificationPreferenceUpdateStore
    );
    this.storeConstructors.set(
      "UserNotificationPreferenceViewStore",
      UserNotificationPreferenceViewStore
    );
    this.storeConstructors.set(
      "UserNotificationPreferenceListStore",
      UserNotificationPreferenceListStore
    );
    this.storeConstructors.set("AlertRuleCreateStore", AlertRuleCreateStore);
    this.storeConstructors.set("AlertRuleUpdateStore", AlertRuleUpdateStore);
    this.storeConstructors.set("AlertRuleViewStore", AlertRuleViewStore);
    this.storeConstructors.set("AlertRuleListStore", AlertRuleListStore);
    this.storeConstructors.set(
      "AlertSubscriptionCreateStore",
      AlertSubscriptionCreateStore
    );
    this.storeConstructors.set(
      "AlertSubscriptionUpdateStore",
      AlertSubscriptionUpdateStore
    );
    this.storeConstructors.set(
      "AlertSubscriptionViewStore",
      AlertSubscriptionViewStore
    );
    this.storeConstructors.set(
      "AlertSubscriptionListStore",
      AlertSubscriptionListStore
    );
    this.storeConstructors.set(
      "EmailVerificationCreateStore",
      EmailVerificationCreateStore
    );
    this.storeConstructors.set(
      "EmailVerificationUpdateStore",
      EmailVerificationUpdateStore
    );
    this.storeConstructors.set(
      "EmailVerificationViewStore",
      EmailVerificationViewStore
    );
    this.storeConstructors.set(
      "EmailVerificationListStore",
      EmailVerificationListStore
    );
    this.storeConstructors.set(
      "ShowcaseWorkflowStateCreateStore",
      ShowcaseWorkflowStateCreateStore
    );
    this.storeConstructors.set(
      "ShowcaseWorkflowStateUpdateStore",
      ShowcaseWorkflowStateUpdateStore
    );
    this.storeConstructors.set(
      "ShowcaseWorkflowStateViewStore",
      ShowcaseWorkflowStateViewStore
    );
    this.storeConstructors.set(
      "ShowcaseWorkflowStateListStore",
      ShowcaseWorkflowStateListStore
    );
    this.storeConstructors.set(
      "ShowcaseCategoryCreateStore",
      ShowcaseCategoryCreateStore
    );
    this.storeConstructors.set(
      "ShowcaseCategoryUpdateStore",
      ShowcaseCategoryUpdateStore
    );
    this.storeConstructors.set(
      "ShowcaseCategoryViewStore",
      ShowcaseCategoryViewStore
    );
    this.storeConstructors.set(
      "ShowcaseCategoryListStore",
      ShowcaseCategoryListStore
    );
    this.storeConstructors.set(
      "ShowcaseLineCreateStore",
      ShowcaseLineCreateStore
    );
    this.storeConstructors.set(
      "ShowcaseLineUpdateStore",
      ShowcaseLineUpdateStore
    );
    this.storeConstructors.set("ShowcaseLineViewStore", ShowcaseLineViewStore);
    this.storeConstructors.set("ShowcaseLineListStore", ShowcaseLineListStore);
    this.storeConstructors.set(
      "DesignShowcaseCreateStore",
      DesignShowcaseCreateStore
    );
    this.storeConstructors.set(
      "DesignShowcaseUpdateStore",
      DesignShowcaseUpdateStore
    );
    this.storeConstructors.set(
      "DesignShowcaseViewStore",
      DesignShowcaseViewStore
    );
    this.storeConstructors.set(
      "DesignShowcaseListStore",
      DesignShowcaseListStore
    );
    this.storeConstructors.set("ThemeViewStore", ThemeViewStore);
    this.storeConstructors.set("ThemeListStore", ThemeListStore);
    this.storeConstructors.set(
      "AuthorComposerCreateStore",
      AuthorComposerCreateStore
    );
    this.storeConstructors.set(
      "AuthorComposerUpdateStore",
      AuthorComposerUpdateStore
    );
    this.storeConstructors.set(
      "AuthorComposerViewStore",
      AuthorComposerViewStore
    );
    this.storeConstructors.set(
      "AuthorComposerListStore",
      AuthorComposerListStore
    );
    this.storeConstructors.set(
      "CollectiveManagementSocietyCreateStore",
      CollectiveManagementSocietyCreateStore
    );
    this.storeConstructors.set(
      "CollectiveManagementSocietyUpdateStore",
      CollectiveManagementSocietyUpdateStore
    );
    this.storeConstructors.set(
      "CollectiveManagementSocietyViewStore",
      CollectiveManagementSocietyViewStore
    );
    this.storeConstructors.set(
      "CollectiveManagementSocietyListStore",
      CollectiveManagementSocietyListStore
    );
    this.storeConstructors.set(
      "AuthorCollectingSocietyCreateStore",
      AuthorCollectingSocietyCreateStore
    );
    this.storeConstructors.set(
      "AuthorCollectingSocietyUpdateStore",
      AuthorCollectingSocietyUpdateStore
    );
    this.storeConstructors.set(
      "AuthorCollectingSocietyViewStore",
      AuthorCollectingSocietyViewStore
    );
    this.storeConstructors.set(
      "AuthorCollectingSocietyListStore",
      AuthorCollectingSocietyListStore
    );
    this.storeConstructors.set("PublisherCreateStore", PublisherCreateStore);
    this.storeConstructors.set("PublisherUpdateStore", PublisherUpdateStore);
    this.storeConstructors.set("PublisherViewStore", PublisherViewStore);
    this.storeConstructors.set("PublisherListStore", PublisherListStore);
    this.storeConstructors.set(
      "AuthorPublisherCreateStore",
      AuthorPublisherCreateStore
    );
    this.storeConstructors.set(
      "AuthorPublisherUpdateStore",
      AuthorPublisherUpdateStore
    );
    this.storeConstructors.set(
      "AuthorPublisherViewStore",
      AuthorPublisherViewStore
    );
    this.storeConstructors.set(
      "AuthorPublisherListStore",
      AuthorPublisherListStore
    );
    this.storeConstructors.set(
      "WorkAuthorPublisherCreateStore",
      WorkAuthorPublisherCreateStore
    );
    this.storeConstructors.set(
      "WorkAuthorPublisherUpdateStore",
      WorkAuthorPublisherUpdateStore
    );
    this.storeConstructors.set(
      "WorkAuthorPublisherViewStore",
      WorkAuthorPublisherViewStore
    );
    this.storeConstructors.set(
      "WorkAuthorPublisherListStore",
      WorkAuthorPublisherListStore
    );
    this.storeConstructors.set(
      "PublisherCollectingSocietyCreateStore",
      PublisherCollectingSocietyCreateStore
    );
    this.storeConstructors.set(
      "PublisherCollectingSocietyUpdateStore",
      PublisherCollectingSocietyUpdateStore
    );
    this.storeConstructors.set(
      "PublisherCollectingSocietyViewStore",
      PublisherCollectingSocietyViewStore
    );
    this.storeConstructors.set(
      "PublisherCollectingSocietyListStore",
      PublisherCollectingSocietyListStore
    );
    this.storeConstructors.set(
      "PublisherRepresentativeCreateStore",
      PublisherRepresentativeCreateStore
    );
    this.storeConstructors.set(
      "PublisherRepresentativeUpdateStore",
      PublisherRepresentativeUpdateStore
    );
    this.storeConstructors.set(
      "PublisherRepresentativeViewStore",
      PublisherRepresentativeViewStore
    );
    this.storeConstructors.set(
      "PublisherRepresentativeListStore",
      PublisherRepresentativeListStore
    );
    this.storeConstructors.set(
      "EditionTypeCreateStore",
      EditionTypeCreateStore
    );
    this.storeConstructors.set(
      "EditionTypeUpdateStore",
      EditionTypeUpdateStore
    );
    this.storeConstructors.set("EditionTypeViewStore", EditionTypeViewStore);
    this.storeConstructors.set("EditionTypeListStore", EditionTypeListStore);
    this.storeConstructors.set(
      "MusicalWorkCreateStore",
      MusicalWorkCreateStore
    );
    this.storeConstructors.set(
      "MusicalWorkUpdateStore",
      MusicalWorkUpdateStore
    );
    this.storeConstructors.set("MusicalWorkViewStore", MusicalWorkViewStore);
    this.storeConstructors.set("MusicalWorkListStore", MusicalWorkListStore);
    this.storeConstructors.set("PhonogramCreateStore", PhonogramCreateStore);
    this.storeConstructors.set("PhonogramUpdateStore", PhonogramUpdateStore);
    this.storeConstructors.set("PhonogramViewStore", PhonogramViewStore);
    this.storeConstructors.set("PhonogramListStore", PhonogramListStore);
    this.storeConstructors.set("TerritoryCreateStore", TerritoryCreateStore);
    this.storeConstructors.set("TerritoryUpdateStore", TerritoryUpdateStore);
    this.storeConstructors.set("TerritoryViewStore", TerritoryViewStore);
    this.storeConstructors.set("TerritoryListStore", TerritoryListStore);
    this.storeConstructors.set(
      "MusicalWorkLinkCreateStore",
      MusicalWorkLinkCreateStore
    );
    this.storeConstructors.set(
      "MusicalWorkLinkUpdateStore",
      MusicalWorkLinkUpdateStore
    );
    this.storeConstructors.set(
      "MusicalWorkLinkViewStore",
      MusicalWorkLinkViewStore
    );
    this.storeConstructors.set(
      "MusicalWorkLinkListStore",
      MusicalWorkLinkListStore
    );
    this.storeConstructors.set("PlatformCreateStore", PlatformCreateStore);
    this.storeConstructors.set("PlatformUpdateStore", PlatformUpdateStore);
    this.storeConstructors.set("PlatformViewStore", PlatformViewStore);
    this.storeConstructors.set("PlatformListStore", PlatformListStore);
    this.storeConstructors.set(
      "PlatformLinkTypeCreateStore",
      PlatformLinkTypeCreateStore
    );
    this.storeConstructors.set(
      "PlatformLinkTypeUpdateStore",
      PlatformLinkTypeUpdateStore
    );
    this.storeConstructors.set(
      "PlatformLinkTypeViewStore",
      PlatformLinkTypeViewStore
    );
    this.storeConstructors.set(
      "PlatformLinkTypeListStore",
      PlatformLinkTypeListStore
    );
    this.storeConstructors.set("WorkAuthorCreateStore", WorkAuthorCreateStore);
    this.storeConstructors.set("WorkAuthorUpdateStore", WorkAuthorUpdateStore);
    this.storeConstructors.set("WorkAuthorViewStore", WorkAuthorViewStore);
    this.storeConstructors.set("WorkAuthorListStore", WorkAuthorListStore);
    this.storeConstructors.set("EditionCreateStore", EditionCreateStore);
    this.storeConstructors.set("EditionUpdateStore", EditionUpdateStore);
    this.storeConstructors.set("EditionViewStore", EditionViewStore);
    this.storeConstructors.set("EditionListStore", EditionListStore);
    this.storeConstructors.set(
      "SignedEditionViewStore",
      SignedEditionViewStore
    );
    this.storeConstructors.set(
      "SignedEditionListStore",
      SignedEditionListStore
    );
    this.storeConstructors.set(
      "EditionContractPublisherViewStore",
      EditionContractPublisherViewStore
    );
    this.storeConstructors.set(
      "EditionContractPublisherListStore",
      EditionContractPublisherListStore
    );
    this.storeConstructors.set(
      "EditionDocumentationViewStore",
      EditionDocumentationViewStore
    );
    this.storeConstructors.set(
      "EditionDocumentationListStore",
      EditionDocumentationListStore
    );
    this.storeConstructors.set(
      "SignedContractViewStore",
      SignedContractViewStore
    );
    this.storeConstructors.set(
      "SignedContractListStore",
      SignedContractListStore
    );
    this.storeConstructors.set(
      "EditionDocumentTypeCreateStore",
      EditionDocumentTypeCreateStore
    );
    this.storeConstructors.set(
      "EditionDocumentTypeUpdateStore",
      EditionDocumentTypeUpdateStore
    );
    this.storeConstructors.set(
      "EditionDocumentTypeViewStore",
      EditionDocumentTypeViewStore
    );
    this.storeConstructors.set(
      "EditionDocumentTypeListStore",
      EditionDocumentTypeListStore
    );
    this.storeConstructors.set(
      "AssociatedDocumentEditionViewStore",
      AssociatedDocumentEditionViewStore
    );
    this.storeConstructors.set(
      "AssociatedDocumentEditionListStore",
      AssociatedDocumentEditionListStore
    );
    this.storeConstructors.set(
      "AssociatedDocumentSignedEditionViewStore",
      AssociatedDocumentSignedEditionViewStore
    );
    this.storeConstructors.set(
      "AssociatedDocumentSignedEditionListStore",
      AssociatedDocumentSignedEditionListStore
    );
    this.storeConstructors.set(
      "SignedEditionBlockchainCertViewStore",
      SignedEditionBlockchainCertViewStore
    );
    this.storeConstructors.set(
      "SignedEditionBlockchainCertListStore",
      SignedEditionBlockchainCertListStore
    );
    this.storeConstructors.set(
      "UserAttachmentEditionCreateStore",
      UserAttachmentEditionCreateStore
    );
    this.storeConstructors.set(
      "UserAttachmentEditionUpdateStore",
      UserAttachmentEditionUpdateStore
    );
    this.storeConstructors.set(
      "UserAttachmentEditionViewStore",
      UserAttachmentEditionViewStore
    );
    this.storeConstructors.set(
      "UserAttachmentEditionListStore",
      UserAttachmentEditionListStore
    );
    this.storeConstructors.set(
      "InterpreterCreateStore",
      InterpreterCreateStore
    );
    this.storeConstructors.set(
      "InterpreterUpdateStore",
      InterpreterUpdateStore
    );
    this.storeConstructors.set("InterpreterViewStore", InterpreterViewStore);
    this.storeConstructors.set("InterpreterListStore", InterpreterListStore);
    this.storeConstructors.set(
      "MusicalGenreCreateStore",
      MusicalGenreCreateStore
    );
    this.storeConstructors.set(
      "MusicalGenreUpdateStore",
      MusicalGenreUpdateStore
    );
    this.storeConstructors.set("MusicalGenreViewStore", MusicalGenreViewStore);
    this.storeConstructors.set("MusicalGenreListStore", MusicalGenreListStore);
    this.storeConstructors.set(
      "ContractObservationCreateStore",
      ContractObservationCreateStore
    );
    this.storeConstructors.set(
      "ContractObservationUpdateStore",
      ContractObservationUpdateStore
    );
    this.storeConstructors.set(
      "ContractObservationViewStore",
      ContractObservationViewStore
    );
    this.storeConstructors.set(
      "ContractObservationListStore",
      ContractObservationListStore
    );
    this.storeConstructors.set(
      "PaymentReceiptTypeCreateStore",
      PaymentReceiptTypeCreateStore
    );
    this.storeConstructors.set(
      "PaymentReceiptTypeUpdateStore",
      PaymentReceiptTypeUpdateStore
    );
    this.storeConstructors.set(
      "PaymentReceiptTypeViewStore",
      PaymentReceiptTypeViewStore
    );
    this.storeConstructors.set(
      "PaymentReceiptTypeListStore",
      PaymentReceiptTypeListStore
    );
    this.storeConstructors.set(
      "PaymentReceiptUseViewStore",
      PaymentReceiptUseViewStore
    );
    this.storeConstructors.set(
      "PaymentReceiptUseListStore",
      PaymentReceiptUseListStore
    );
    this.storeConstructors.set(
      "PaymentReceiptCreateStore",
      PaymentReceiptCreateStore
    );
    this.storeConstructors.set(
      "PaymentReceiptUpdateStore",
      PaymentReceiptUpdateStore
    );
    this.storeConstructors.set(
      "PaymentReceiptViewStore",
      PaymentReceiptViewStore
    );
    this.storeConstructors.set(
      "PaymentReceiptListStore",
      PaymentReceiptListStore
    );
    this.storeConstructors.set(
      "TadManagerConfigUpdateStore",
      TadManagerConfigUpdateStore
    );
    this.storeConstructors.set(
      "TadManagerConfigViewStore",
      TadManagerConfigViewStore
    );
    this.storeConstructors.set(
      "TadManagerConfigListStore",
      TadManagerConfigListStore
    );
    this.storeConstructors.set(
      "DocumentGenerationParamsCreateStore",
      DocumentGenerationParamsCreateStore
    );
    this.storeConstructors.set(
      "DocumentGenerationParamsUpdateStore",
      DocumentGenerationParamsUpdateStore
    );
    this.storeConstructors.set(
      "DocumentGenerationParamsViewStore",
      DocumentGenerationParamsViewStore
    );
    this.storeConstructors.set(
      "DocumentGenerationParamsListStore",
      DocumentGenerationParamsListStore
    );
    this.storeConstructors.set(
      "EditionDocumentationWorkflowStateCreateStore",
      EditionDocumentationWorkflowStateCreateStore
    );
    this.storeConstructors.set(
      "EditionDocumentationWorkflowStateUpdateStore",
      EditionDocumentationWorkflowStateUpdateStore
    );
    this.storeConstructors.set(
      "EditionDocumentationWorkflowStateViewStore",
      EditionDocumentationWorkflowStateViewStore
    );
    this.storeConstructors.set(
      "EditionDocumentationWorkflowStateListStore",
      EditionDocumentationWorkflowStateListStore
    );
    this.storeConstructors.set(
      "SignedEditionDndaWorkflowStateCreateStore",
      SignedEditionDndaWorkflowStateCreateStore
    );
    this.storeConstructors.set(
      "SignedEditionDndaWorkflowStateUpdateStore",
      SignedEditionDndaWorkflowStateUpdateStore
    );
    this.storeConstructors.set(
      "SignedEditionDndaWorkflowStateViewStore",
      SignedEditionDndaWorkflowStateViewStore
    );
    this.storeConstructors.set(
      "SignedEditionDndaWorkflowStateListStore",
      SignedEditionDndaWorkflowStateListStore
    );
    this.storeConstructors.set(
      "SignedEditionSadaicWorkflowStateCreateStore",
      SignedEditionSadaicWorkflowStateCreateStore
    );
    this.storeConstructors.set(
      "SignedEditionSadaicWorkflowStateUpdateStore",
      SignedEditionSadaicWorkflowStateUpdateStore
    );
    this.storeConstructors.set(
      "SignedEditionSadaicWorkflowStateViewStore",
      SignedEditionSadaicWorkflowStateViewStore
    );
    this.storeConstructors.set(
      "SignedEditionSadaicWorkflowStateListStore",
      SignedEditionSadaicWorkflowStateListStore
    );
    this.storeConstructors.set(
      "LlmProviderCreateStore",
      LlmProviderCreateStore
    );
    this.storeConstructors.set(
      "LlmProviderUpdateStore",
      LlmProviderUpdateStore
    );
    this.storeConstructors.set("LlmProviderViewStore", LlmProviderViewStore);
    this.storeConstructors.set("LlmProviderListStore", LlmProviderListStore);
    this.storeConstructors.set(
      "TenantLlmConfigCreateStore",
      TenantLlmConfigCreateStore
    );
    this.storeConstructors.set(
      "TenantLlmConfigUpdateStore",
      TenantLlmConfigUpdateStore
    );
    this.storeConstructors.set(
      "TenantLlmConfigViewStore",
      TenantLlmConfigViewStore
    );
    this.storeConstructors.set(
      "TenantLlmConfigListStore",
      TenantLlmConfigListStore
    );
    this.storeConstructors.set(
      "TenantDocumentCreateStore",
      TenantDocumentCreateStore
    );
    this.storeConstructors.set(
      "TenantDocumentUpdateStore",
      TenantDocumentUpdateStore
    );
    this.storeConstructors.set(
      "TenantDocumentViewStore",
      TenantDocumentViewStore
    );
    this.storeConstructors.set(
      "TenantDocumentListStore",
      TenantDocumentListStore
    );
    this.storeConstructors.set(
      "AiSearchConversationViewStore",
      AiSearchConversationViewStore
    );
    this.storeConstructors.set(
      "AiSearchConversationListStore",
      AiSearchConversationListStore
    );
    this.storeConstructors.set(
      "AiSearchResponseCreateStore",
      AiSearchResponseCreateStore
    );
    this.storeConstructors.set(
      "AiSearchResponseViewStore",
      AiSearchResponseViewStore
    );
    this.storeConstructors.set(
      "AiSearchResponseListStore",
      AiSearchResponseListStore
    );
    this.storeConstructors.set(
      "AiSearchToolCallCreateStore",
      AiSearchToolCallCreateStore
    );
    this.storeConstructors.set(
      "AiSearchToolCallUpdateStore",
      AiSearchToolCallUpdateStore
    );
    this.storeConstructors.set(
      "AiSearchToolCallViewStore",
      AiSearchToolCallViewStore
    );
    this.storeConstructors.set(
      "AiSearchToolCallListStore",
      AiSearchToolCallListStore
    );
    this.storeConstructors.set(
      "TenantTokenUsageViewStore",
      TenantTokenUsageViewStore
    );
    this.storeConstructors.set(
      "TenantTokenUsageListStore",
      TenantTokenUsageListStore
    );
    this.storeConstructors.set(
      "TenantStorageUsageViewStore",
      TenantStorageUsageViewStore
    );
    this.storeConstructors.set(
      "TenantStorageUsageListStore",
      TenantStorageUsageListStore
    );
    this.storeConstructors.set(
      "AiSearchAutoEvalViewStore",
      AiSearchAutoEvalViewStore
    );
    this.storeConstructors.set(
      "AiSearchAutoEvalListStore",
      AiSearchAutoEvalListStore
    );
    this.storeConstructors.set(
      "AiSearchPatternCreateStore",
      AiSearchPatternCreateStore
    );
    this.storeConstructors.set(
      "AiSearchPatternUpdateStore",
      AiSearchPatternUpdateStore
    );
    this.storeConstructors.set(
      "AiSearchPatternViewStore",
      AiSearchPatternViewStore
    );
    this.storeConstructors.set(
      "AiSearchPatternListStore",
      AiSearchPatternListStore
    );
    this.storeConstructors.set(
      "EvaluationQueryCreateStore",
      EvaluationQueryCreateStore
    );
    this.storeConstructors.set(
      "EvaluationQueryUpdateStore",
      EvaluationQueryUpdateStore
    );
    this.storeConstructors.set(
      "EvaluationQueryViewStore",
      EvaluationQueryViewStore
    );
    this.storeConstructors.set(
      "EvaluationQueryListStore",
      EvaluationQueryListStore
    );
    this.storeConstructors.set(
      "QueryPatternCreateStore",
      QueryPatternCreateStore
    );
    this.storeConstructors.set(
      "QueryPatternUpdateStore",
      QueryPatternUpdateStore
    );
    this.storeConstructors.set("QueryPatternViewStore", QueryPatternViewStore);
    this.storeConstructors.set("QueryPatternListStore", QueryPatternListStore);
    this.storeConstructors.set(
      "EsignatureConfigCreateStore",
      EsignatureConfigCreateStore
    );
    this.storeConstructors.set(
      "EsignatureConfigUpdateStore",
      EsignatureConfigUpdateStore
    );
    this.storeConstructors.set(
      "EsignatureConfigViewStore",
      EsignatureConfigViewStore
    );
    this.storeConstructors.set(
      "EsignatureConfigListStore",
      EsignatureConfigListStore
    );
    this.storeConstructors.set(
      "EsignatureDocumentViewStore",
      EsignatureDocumentViewStore
    );
    this.storeConstructors.set(
      "EsignatureDocumentListStore",
      EsignatureDocumentListStore
    );
    this.storeConstructors.set(
      "EsignatureRecipientViewStore",
      EsignatureRecipientViewStore
    );
    this.storeConstructors.set(
      "EsignatureRecipientListStore",
      EsignatureRecipientListStore
    );
    this.storeConstructors.set(
      "EsignatureWebhookEventViewStore",
      EsignatureWebhookEventViewStore
    );
    this.storeConstructors.set(
      "EsignatureWebhookEventListStore",
      EsignatureWebhookEventListStore
    );
  }

  getStoreConstructor(storeName: string): any {
    return this.storeConstructors.get(storeName);
  }

  getCurrentPageStore(): any {
    const pathname = this.location?.pathname || "";
    return this.storeRegistry.getStoreIfExists(pathname);
  }
}
